import React from "react";
import {
    Checkbox,
    FormControlLabel,
    FormGroup,
    Radio,
    RadioGroup,
    TextField,
} from "@mui/material";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import moment from "moment";
import {
    AirlineMode,
    AirlineRestrictionFields,
    PreferredAirline,
} from "./AirlineRestrictionFields";

export type DaysState = {
    MONDAY: boolean;
    TUESDAY: boolean;
    WEDNESDAY: boolean;
    THURSDAY: boolean;
    FRIDAY: boolean;
    SATURDAY: boolean;
    SUNDAY: boolean;
};

type SunquestCacheFormSectionProps = {
    number: number;
    title: string;
    children: React.ReactNode;
};

export const SunquestCacheFormSection = ({
    number,
    title,
    children,
}: SunquestCacheFormSectionProps) => (
    <section className="sunquestFormSection">
        <header className="sunquestFormSection__header">
            <span className="sunquestFormSection__badge">{number}</span>
            <h3 className="sunquestFormSection__title">{title}</h3>
        </header>
        <div className="sunquestFormSection__body">{children}</div>
    </section>
);

type FieldProps = {
    label: string;
    children: React.ReactNode;
    className?: string;
};

const FormField = ({ label, children, className }: FieldProps) => (
    <div className={`sunquestFormField ${className || ""}`}>
        <label className="sunquestFormField__label">{label}</label>
        <div className="sunquestFormField__control">{children}</div>
    </div>
);

export type SunquestCacheFormProps = {
    mode: "create" | "edit";
    origin: string;
    destination: string;
    onOriginChange: (value: string) => void;
    onDestinationChange: (value: string) => void;
    departureStart: string;
    departureEnd: string;
    onDepartureStartChange: (date: any) => void;
    onDepartureEndChange: (date: any) => void;
    maxStay: number;
    onMaxStayChange: (value: string) => void;
    days: DaysState;
    daysShow: boolean;
    onDaysShowChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onDayToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onRepeatsHandle: (event: React.ChangeEvent<HTMLInputElement>) => void;
    repeats: any[];
    tripType: string;
    onTripTypeChange: (value: string) => void;
    unavailablePaxTypes: string[];
    onUnavailablePaxTypesChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    airlineMode: AirlineMode;
    preferredAirlines: PreferredAirline[];
    prohibitedAirlines: string[];
    onAirlineModeChange: (mode: AirlineMode) => void;
    onPreferredChange: (rows: PreferredAirline[]) => void;
    onProhibitedChange: (codes: string[]) => void;
    onSave: () => void;
    onCancel: () => void;
};

const getRepeatRadioValue = (repeats: any[], daysShow: boolean, days: DaysState): string => {
    if (daysShow || Object.values(days).some(Boolean)) return "selectedDays";
    if (!Array.isArray(repeats) || !repeats.length) return "";
    const normalized = repeats
        .map((r) => String(r || "").toUpperCase())
        .filter(Boolean);
    if (!normalized.length) return "";
    if (normalized.includes("NEVER")) return "NEVER";
    if (normalized.includes("EVERYDAY")) return "EVERYDAY";
    return "selectedDays";
};

export const SunquestCacheForm = ({
    mode,
    origin,
    destination,
    onOriginChange,
    onDestinationChange,
    departureStart,
    departureEnd,
    onDepartureStartChange,
    onDepartureEndChange,
    maxStay,
    onMaxStayChange,
    days,
    daysShow,
    onDaysShowChange,
    onDayToggle,
    onRepeatsHandle,
    repeats,
    tripType,
    onTripTypeChange,
    unavailablePaxTypes,
    onUnavailablePaxTypesChange,
    airlineMode,
    preferredAirlines,
    prohibitedAirlines,
    onAirlineModeChange,
    onPreferredChange,
    onProhibitedChange,
    onSave,
    onCancel,
}: SunquestCacheFormProps) => {
    const showSelectedDays = daysShow || Object.values(days).some(Boolean);
    const repeatValue = getRepeatRadioValue(repeats, daysShow, days);

    return (
        <div className={`sunquestCacheForm sunquestCacheForm--${mode}`}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <div className="sunquestCacheForm__scroll">
            <SunquestCacheFormSection number={1} title="Route & Dates">
                <div className="sunquestFormGrid sunquestFormGrid--4">
                    <FormField label="Origin">
                        <TextField
                            type="text"
                            variant="outlined"
                            className="form-control"
                            value={origin}
                            onChange={(e) =>
                                onOriginChange(
                                    mode === "create"
                                        ? String(e.target.value).toUpperCase()
                                        : e.target.value
                                )
                            }
                        />
                    </FormField>
                    <FormField label="Destination">
                        <TextField
                            type="text"
                            variant="outlined"
                            className="form-control"
                            value={destination}
                            onChange={(e) =>
                                onDestinationChange(
                                    mode === "create"
                                        ? String(e.target.value).toUpperCase()
                                        : e.target.value
                                )
                            }
                        />
                    </FormField>
                    <FormField label="Departure Starts">
                        <KeyboardDatePicker
                            value={
                                departureStart
                                    ? moment(departureStart, "DD-MMM-YYYY").startOf("day").toDate()
                                    : null
                            }
                            onChange={onDepartureStartChange}
                            className="form-control"
                            format="dd MMM yyyy"
                            invalidDateMessage={null}
                            minDateMessage={null}
                            maxDateMessage={null}
                        />
                    </FormField>
                    <FormField label="Departure Ends">
                        <KeyboardDatePicker
                            value={
                                departureEnd
                                    ? moment(departureEnd, "DD-MMM-YYYY").startOf("day").toDate()
                                    : null
                            }
                            onChange={onDepartureEndChange}
                            className="form-control"
                            format="dd MMM yyyy"
                            invalidDateMessage={null}
                            minDateMessage={null}
                            maxDateMessage={null}
                        />
                    </FormField>
                </div>
            </SunquestCacheFormSection>

            <SunquestCacheFormSection number={2} title="Stay & Schedule">
                <div className="sunquestFormGrid sunquestFormGrid--2">
                    <FormField label="Max Stay">
                        <select
                            className="form-control"
                            value={maxStay}
                            onChange={(e) => onMaxStayChange(e.target.value)}
                        >
                            {[...Array(10)].map((_, index) => (
                                <option key={index + 1} value={index + 1}>
                                    {index + 1}
                                </option>
                            ))}
                        </select>
                    </FormField>
                    <FormField label="Repeat" className="sunquestFormField--wide">
                        <RadioGroup
                            aria-label="position"
                            className="sun__radioBtn sun__radioBtn--inline"
                            name="position"
                            value={repeatValue}
                            onChange={onDaysShowChange}
                        >
                            <FormControlLabel
                                value="NEVER"
                                control={<Radio color="primary" size="small" />}
                                label="Never"
                            />
                            <FormControlLabel
                                value="EVERYDAY"
                                control={<Radio color="primary" size="small" />}
                                label="Everyday"
                            />
                            <FormControlLabel
                                value="selectedDays"
                                control={<Radio color="primary" size="small" />}
                                label="Selected Days"
                            />
                        </RadioGroup>
                        {showSelectedDays && (
                            <FormGroup
                                className="sun__checkBox sun__checkBox--inline"
                                onChange={onRepeatsHandle}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={days.MONDAY}
                                            onChange={onDayToggle}
                                            name="MONDAY"
                                            color="primary"
                                        />
                                    }
                                    label="Monday"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={days.TUESDAY}
                                            onChange={onDayToggle}
                                            name="TUESDAY"
                                            color="primary"
                                        />
                                    }
                                    label="Tuesday"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={days.WEDNESDAY}
                                            onChange={onDayToggle}
                                            name="WEDNESDAY"
                                            color="primary"
                                        />
                                    }
                                    label="Wednesday"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={days.THURSDAY}
                                            onChange={onDayToggle}
                                            name="THURSDAY"
                                            color="primary"
                                        />
                                    }
                                    label="Thursday"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={days.FRIDAY}
                                            onChange={onDayToggle}
                                            name="FRIDAY"
                                            color="primary"
                                        />
                                    }
                                    label="Friday"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={days.SATURDAY}
                                            onChange={onDayToggle}
                                            name="SATURDAY"
                                            color="primary"
                                        />
                                    }
                                    label="Saturday"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={days.SUNDAY}
                                            onChange={onDayToggle}
                                            name="SUNDAY"
                                            color="primary"
                                        />
                                    }
                                    label="Sunday"
                                />
                            </FormGroup>
                        )}
                    </FormField>
                </div>
            </SunquestCacheFormSection>

            <SunquestCacheFormSection number={3} title="Trip Configuration">
                <FormField label="Trip Type">
                    <RadioGroup
                        aria-label="tripType"
                        className="sun__radioBtn sun__radioBtn--inline"
                        name="tripType"
                        value={tripType}
                        onChange={(e) =>
                            onTripTypeChange(String((e.target as HTMLInputElement).value))
                        }
                    >
                        <FormControlLabel
                            value="TOW"
                            control={<Radio color="primary" size="small" />}
                            label="TOW"
                        />
                        <FormControlLabel
                            value="RT"
                            control={<Radio color="primary" size="small" />}
                            label="RT"
                        />
                        <FormControlLabel
                            value="BOTH"
                            control={<Radio color="primary" size="small" />}
                            label="BOTH"
                        />
                    </RadioGroup>
                </FormField>
            </SunquestCacheFormSection>

            <SunquestCacheFormSection number={4} title="Restrictions">
                <div className="sunquestFormGrid sunquestFormGrid--2">
                    <FormField label="Unavailable Pax Types">
                        <FormGroup className="sun__checkBox sun__checkBox--inline">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={unavailablePaxTypes.includes("PFA")}
                                        onChange={onUnavailablePaxTypesChange}
                                        name="PFA"
                                        color="primary"
                                    />
                                }
                                label="PFA"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={unavailablePaxTypes.includes("ITX")}
                                        onChange={onUnavailablePaxTypesChange}
                                        name="ITX"
                                        color="primary"
                                    />
                                }
                                label="ITX"
                            />
                        </FormGroup>
                    </FormField>
                    <FormField label="Airlines" className="sunquestFormField--airlines">
                        <AirlineRestrictionFields
                            airlineMode={airlineMode}
                            preferredAirlines={preferredAirlines}
                            prohibitedAirlines={prohibitedAirlines}
                            onModeChange={onAirlineModeChange}
                            onPreferredChange={onPreferredChange}
                            onProhibitedChange={onProhibitedChange}
                        />
                    </FormField>
                </div>
            </SunquestCacheFormSection>
            </div>

            <div className="sunquestCacheForm__actions">
                <button type="button" className="cm-secondary-btn cancelBtn" onClick={onCancel}>
                    <i className="far fa-window-close" /> Cancel
                </button>
                <button type="button" className="cm-secondary-btn startBtn" onClick={onSave}>
                    <i className="fa fa-save" /> Save
                </button>
            </div>
            </MuiPickersUtilsProvider>
        </div>
    );
};
